import { setEntitlements } from "./playerSlice";

// SCO-2495: the native shells put paidPlayer / paidOrganizer on the webview
// URL, but only MainProfile ever copied them into the player slice — a page
// that loaded before Profile (share link → FAT → roster) gated on a stale
// localStorage copy. One resolver, run at boot (index.jsx) and on every route
// change (EntitlementSync).
//
// A declared paid value always writes true. A declared "No" is written as
// false ONLY for shells that cannot emit a transient No at cold launch
// (SCO-2502): iOS builds from the one carrying scoreholio-ios PR #29 (the
// pre-auth RevenueCat push no longer writes No), Android builds from the
// SCO-2318 decider. Everything else — older builds, plain web, a missing or
// malformed build — stays upgrade-only, so a wrong No can never lock a paid
// user. Downgrades then take effect on the next webview load, same as
// upgrades. An absent flag leaves the store alone. `spr` is a roster-only
// override and is deliberately not synced.
export const PAID_VALUES = ["Yes", "yes", "true", "1"];

// Rollout gate + rollback switch. null = never honor a declared No on that
// platform. Set each floor to the first build that ships the native fix.
export const HONOR_DECLARED_NO_FLOORS = { ios: null, android: null };

export function isPaidValue(value) {
  return PAID_VALUES.includes(value);
}

// Exact platform match + integer build >= that platform's floor.
export function honorsDeclaredNo(params, floors = HONOR_DECLARED_NO_FLOORS) {
  const floor = floors[params.get("platform")];
  if (floor === null || floor === undefined) return false;
  const build = params.get("build") || "";
  if (!/^\d+$/.test(build)) return false;
  return Number(build) >= floor;
}

// Only the flags the URL declares are in the patch; a non-paid value is
// included (as false) only when the shell passes honorsDeclaredNo.
export function entitlementsFromParams(
  params,
  floors = HONOR_DECLARED_NO_FLOORS,
) {
  const honorNo = honorsDeclaredNo(params, floors);
  const patch = {};
  for (const key of ["paidPlayer", "paidOrganizer"]) {
    if (!params.has(key)) continue;
    if (isPaidValue(params.get(key))) patch[key] = true;
    else if (honorNo) patch[key] = false;
  }
  return patch;
}

// Returns the dispatched patch, or null when the URL declared nothing usable.
export function syncEntitlementsFromUrl(
  dispatch,
  search = window.location.search,
) {
  const patch = entitlementsFromParams(new URLSearchParams(search));
  if (!("paidPlayer" in patch) && !("paidOrganizer" in patch)) return null;
  dispatch(setEntitlements(patch));
  return patch;
}

// SCO-2502: React-built child URLs (home-events menu → roster) forward the
// shell's paid context so the child's boot sync sees what the parent saw.
// Only params that are PRESENT are forwarded — presence is authoritative, so
// inventing an empty `paidPlayer=` would read as a declared No.
export const ENTITLEMENT_CONTEXT_PARAMS = ["paidPlayer", "paidOrganizer", "build"];

export function forwardEntitlementParams(params) {
  return ENTITLEMENT_CONTEXT_PARAMS.filter((k) => params.has(k))
    .map((k) => `&${k}=${encodeURIComponent(params.get(k))}`)
    .join("");
}
